import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { Organisation } from 'store/organisation/models';
import { PwaAddEntitySheet } from './PwaAddEntitySheet';
import { PwaIcon } from './Icon';

type PwaEntityDisconnectedProps = {
  organisation: Organisation;
  /** @deprecated Kept for call-site compat; reconnect opens the PWA add-entity sheet. */
  onReconnect?: () => void;
};

const providerLabelKey = (providerName: string | undefined): string => {
  const p = (providerName || '').toLowerCase().trim();
  if (p.includes('quickbook')) return 'common.quickbooks';
  if (p === 'xero') return 'common.xero';
  if (p === 'zoho') return 'common.zoho';
  if (p === 'dear' || p === 'cin7') return 'common.cin7';
  return 'common.account';
};

export const PwaEntityDisconnected: React.FC<PwaEntityDisconnectedProps> = ({
  organisation,
}) => {
  const { t } = useTranslation();
  const [addEntityOpen, setAddEntityOpen] = React.useState(false);

  const providerKey = providerLabelKey(organisation.providerName);
  const providerName = t(providerKey, {
    defaultValue: organisation.providerName || t('common.account'),
  });

  return (
    <div className="pwa-entity-disconnected" data-shell="pwa">
      <main className="pwa-main pwa-entity-disconnected__main">
        <div style={{ height: 16 }} />
        <section className="pwa-section">
          <div className="pwa-banner danger" role="status">
            <PwaIcon name="link" size={22} />
            <div className="pwa-banner__body">
              <div>
                <h3>
                  {t('organisation.pwa-entity-disconnected-title', {
                    provider: providerName,
                  })}
                </h3>
                <p>{t('organisation.pwa-entity-disconnected-body')}</p>
              </div>
              <button
                type="button"
                className="pwa-btn primary block"
                onClick={() => setAddEntityOpen(true)}
              >
                <PwaIcon name="refresh" size={19} />
                {t('organisation.pwa-entity-disconnected-reconnect', {
                  provider: providerName,
                })}
              </button>
            </div>
          </div>
        </section>

        <section className="pwa-section">
          <div className="pwa-section-head">
            <span>{t('organisation.pwa-entity-disconnected-paused')}</span>
          </div>
          <div className="pwa-list pwa-disabled" aria-hidden="true">
            <div className="pwa-row static">
              <span className="pwa-row-ico neutral">
                <PwaIcon name="camera" size={20} />
              </span>
              <span className="pwa-row-body">
                <span className="pwa-row-title">
                  {t('organisation.pwa-entity-disconnected-capture')}
                </span>
                <span className="pwa-row-sub">
                  {t('organisation.pwa-entity-disconnected-capture-sub')}
                </span>
              </span>
            </div>
            <div className="pwa-row static">
              <span className="pwa-row-ico neutral">
                <PwaIcon name="review" size={20} />
              </span>
              <span className="pwa-row-body">
                <span className="pwa-row-title">
                  {t('organisation.pwa-entity-disconnected-review')}
                </span>
                <span className="pwa-row-sub">
                  {t('organisation.pwa-entity-disconnected-review-sub', {
                    provider: providerName,
                  })}
                </span>
              </span>
            </div>
            <div className="pwa-row static">
              <span className="pwa-row-ico neutral">
                <PwaIcon name="returns" size={20} />
              </span>
              <span className="pwa-row-body">
                <span className="pwa-row-title">
                  {t('organisation.pwa-entity-disconnected-returns')}
                </span>
                <span className="pwa-row-sub">
                  {t('organisation.pwa-entity-disconnected-returns-sub')}
                </span>
              </span>
            </div>
          </div>
        </section>
      </main>

      <PwaAddEntitySheet
        open={addEntityOpen}
        onClose={() => setAddEntityOpen(false)}
      />
    </div>
  );
};

export default PwaEntityDisconnected;
